// @ts-check
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';

import ModelType from 'components/Model/ModelType';
import EditItemModal from 'components/Modals/EditItemModal';

import { useTranslation } from 'localization/useTranslation';
import T from 'localization/LocalizationKeys';

import { panelObjects } from './panels';
import dataSources from './datasources';
import { PanelPropType } from './DashboardPropTypes';
import PropertyConditionComponent from 'components/Model/Components/PropertyConditionComponent';


/**
 * @constant
 * @type {Model[]}
 */
const DEFAULT_PANEL_MODELS = [
  {
    type: ModelType.TEXT,
    path: 'title',
    label: T.dashboard.editor.inputs.title.title,
    placeholder: T.dashboard.editor.inputs.title.placeholder
  },
  {
    type: ModelType.TEXTAREA,
    path: 'help',
    label: T.dashboard.editor.inputs.help.title,
    placeholder: T.dashboard.editor.inputs.help.placeholder
  },
  {
    type: PropertyConditionComponent,
    path: 'conditions',
    label: T.dashboard.editor.inputs.visibilityconditions.title,
    disableTitle: true
  }
];

const createPanelModels = (panel) => {
  const panelComponent = panel?.type ? panelObjects[panel?.type] : null;

  const dataSourceProperties = _.isFunction(panelComponent?.properties) ? panelComponent?.properties(panel.properties) : panelComponent?.properties;
  const dataSourceModels = _.flatMap(dataSourceProperties, (property, propertyName) => {
    const mergedDataPanelProperties = _.merge({}, dataSources[property.type].source.defaultValues ?? {}, _.get(panel.properties, propertyName));
    const models = _.isFunction(dataSources[property.type].source.models) ?
      dataSources[property.type].source.models(mergedDataPanelProperties) : dataSources[property.type].source.models;
    return _.map(models, model => ({ ...model, path: 'properties.' + propertyName + '.' + model.path }));
  });
  const panelModels = _.isFunction(panelComponent?.models) ? panelComponent?.models(panel.properties) : panelComponent?.models;
  return [
    ...DEFAULT_PANEL_MODELS,
    ..._.map(panelModels, model => {
      // All models inside the panels needs have 'properties.' prefixed because they are going to modify the
      // 'properties' object under the panel, and not the properties directly under panel (e.g 'title' ).
      return { ...model, path: 'properties.' + model.path };
    }),
    ...dataSourceModels
  ];
};

const panelProperties = (panel) => {
  const panelComponent = panelObjects[panel.type];
  return _.isFunction(panelComponent?.properties) ? panelComponent?.properties(panel.properties) : panelComponent?.properties;
};

export const createPanel = (panelType) => {
  const newPanel = { type: panelType };
  const dataSourceProperties = panelProperties(newPanel);

  // Setup default panel properties from the component
  newPanel.properties = _.cloneDeep(dataSourceProperties);
  return newPanel;
};

const DashboardPanelEditor = ({ panel, onPanelChanged, onDeletePanel }) => {
  const T = useTranslation();
  // Keep a copy of the panel, and only commit it when the dialog closes
  const [tmpPanel, setTmpPanel] = useState(_.cloneDeep(panel));

  useEffect(() => {
    setTmpPanel(_.cloneDeep(panel));
  }, [panel]);

  const models = useMemo(() => createPanelModels(tmpPanel), [tmpPanel]);

  const deselectPanel = useCallback(() => {
    onPanelChanged(null);
  }, [onPanelChanged]);

  const _onPanelChanged = useCallback((newPanel) => {
    // Merge default properties with the new properties
    // This also assigns 'type', which fixes issues with new data sources being added
    // to an existing panel
    const modifiedNewPanel = _.cloneDeep(newPanel);
    const newProperties = panelProperties(modifiedNewPanel);
    _.merge(modifiedNewPanel.properties, newProperties);
    onPanelChanged(modifiedNewPanel);
    deselectPanel();

  }, [deselectPanel, onPanelChanged]);


  const _onDeletePanel = useCallback(() => onDeletePanel(panel), [onDeletePanel, panel]);
  const help = panel?.type ? panelObjects[panel?.type]?.info.help : null;

  return (
    <EditItemModal
      title={T.dashboard.editor.modal.editpanel}
      help={help}
      isOpen={tmpPanel != null}
      models={models}
      item={tmpPanel}
      setItem={setTmpPanel}
      onSave={_onPanelChanged}
      onDelete={_onDeletePanel}
      deleteEnabled
      onCancel={deselectPanel} />
  );
};

DashboardPanelEditor.propTypes = {
  panel: PanelPropType,
  onPanelChanged: PropTypes.func,
  onDeletePanel: PropTypes.func
};

export default React.memo(DashboardPanelEditor);
